import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { SetterOrUpdater, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { isEqual } from "lodash"
import { Annotation } from "../Managers/VisualizationManager/Variables/Annotations"
import { activeAnnotationSessionAtom, annotationsAtom, eventReviewSourceGroupAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Annotations"
import { navigationCursorAtom } from "../Pages/Data/Visualize/DataReview/Atoms/NavigationCursor"
import { linkedWindowJumpTypeAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Timeline"
import { AnnotationGroup } from "../Pages/Data/Visualize/DataReview/Types/Annotations"
import { selectedLayoutGroupAtom, selectedLayoutIdAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Layout"
import { EventReviewConfig, eventReviewConfigsAtom } from "../Pages/Data/Visualize/DataReview/Atoms/EventReview"
import { LinkedWindowEventType } from "../Pages/Data/Visualize/DataReview/Types/LinkedWindowInfo"
import { jumpAllWindows } from "../Pages/Data/Visualize/DataReview/Atoms/Visualizations"
import {
    AnnotationReview,
    AnnotationReviews,
    EBOOSTQuestionID,
    FREQUENCY_OPTIONS,
    IICPatternType,
    PREVALENCE_OPTIONS,
    QuestionResponseType,
} from "../Pages/Data/Visualize/DataReview/Types/EventReview"
import { useBackendLinksProvider } from "./BackendLinksProvider"
import { useEndpointProvider } from "./EndpointProvider"
import { AsyncTask, useAsyncTask } from "../Hooks/useAsyncTask"
import { useAuthProvider } from "./AuthProvider"
import { useEventReviewFormState } from "./useEventReviewFormState"

const isExpectedValidationSaveFailure = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)
    return message.startsWith("Save failed:")
}

export type EventReviewSessionValue = {
    isActive: boolean
    setIsActive: Dispatch<SetStateAction<boolean>>
    sourceGroup: AnnotationGroup | null
    currentAnnotation: Annotation | undefined
    setCurrentAnnotation: Dispatch<SetStateAction<Annotation | undefined>>
    currentAnnotationReview: AnnotationReview | undefined
    updateCurrentAnnotationReview: (review: AnnotationReview | undefined) => void
    sortedAnnotations: Annotation[]
    annotationIndex: number
    totalEvents: number
    annotationReviews: AnnotationReviews
    setAnnotationReviews: Dispatch<SetStateAction<AnnotationReviews>>
    addAnnotationReviewEntry: (review: AnnotationReview) => Promise<any>
    annotationsForRendering: Annotation[]
    exitEventReview: () => void
    unmodifiedAnnotationReview: AnnotationReview | undefined
    filters: EventReviewConfig
    setFilters: SetterOrUpdater<EventReviewConfig>
    availableTags: string[]
    isRequestingReviews: boolean
    reviewedEventIds: Set<number>
    incompleteEventCount: number
}

export type EventReviewFormValue = ReturnType<typeof useEventReviewFormState> & {
    navigateToAnnotation: (annotation: Annotation, noSave?: boolean) => void
    goToNextAnnotation: () => void
    goToPreviousAnnotation: () => void
    saveReviewTask: AsyncTask
}

function sortAnnotations(rawAnnotations: Annotation[]): Annotation[] {
    const annotationsCopy = [...rawAnnotations]
    annotationsCopy.sort((annotation1, annotation2) => {
        if (annotation1.start_time < annotation2.start_time) {
            return -1
        }
        return 1
    })
    return annotationsCopy
}

const EventReviewSessionContext = createContext<EventReviewSessionValue>({} as EventReviewSessionValue)
const EventReviewFormContext = createContext<EventReviewFormValue>({} as EventReviewFormValue)

export const EventReviewProvider: React.FC = ({ children }) => {
    const authProvider = useAuthProvider()
    const backendLinksProvider = useBackendLinksProvider()
    const endpointProvider = useEndpointProvider()
    const selectedLayoutId = useRecoilValue(selectedLayoutIdAtom)
    const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
    const [isActive, setIsActive] = useState(false)
    const sourceGroup = useRecoilValue(eventReviewSourceGroupAtom)
    const setSourceGroup = useSetRecoilState(eventReviewSourceGroupAtom)
    const activeSession = useRecoilValue(activeAnnotationSessionAtom)
    const setActiveSession = useSetRecoilState(activeAnnotationSessionAtom)
    const setAnnotations = useSetRecoilState(annotationsAtom)
    const setLinkedWindowJumpType = useSetRecoilState(linkedWindowJumpTypeAtom)
    const setNavigationCursor = useSetRecoilState(navigationCursorAtom)
    const [currentAnnotationReview, setCurrentAnnotationReview] = useState<AnnotationReview | undefined>()
    const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | undefined>()
    const [unmodifiedAnnotationReview, setUnmodifiedAnnotationReview] = useState<AnnotationReview | undefined>()
    const [annotationReviews, setAnnotationReviews] = useState<AnnotationReviews>([])
    const [filters, setFilters] = useRecoilState(eventReviewConfigsAtom(selectedLayoutId ?? ""))
    const [isRequestingReviews, setIsRequestingReviews] = useState(false)

    const annotations = useRecoilValue(annotationsAtom)
	const reviewableAnnotations = useMemo(
		() => annotations.filter(annotation => !annotation.read_only),
		[annotations]
	)
	const sourceAnnotations = useMemo(() => {
		if (!isActive || !sourceGroup) return reviewableAnnotations
		return reviewableAnnotations.filter(annotation => annotation.group_id?.toString() === sourceGroup.id.toString())
	}, [isActive, reviewableAnnotations, sourceGroup])

    const availableTags = useMemo(() =>
		Array.from(new Set(sourceAnnotations.flatMap(annotation => annotation.tags))).sort()
	, [sourceAnnotations])

    const annotationsForRendering = useMemo(() => {
        if (!isActive || !activeSession) return annotations
        return annotations.filter(a => a.group_id?.toString() === activeSession.id.toString())
    }, [annotations, isActive, activeSession])

    const filteredAnnotations = useMemo(() => {
		let filtered = [...sourceAnnotations]
        const { includeTags, excludeTags } = filters

        if (includeTags?.length > 0) {
            const includeSet = new Set(includeTags.map(tag => tag.toLowerCase()))
            filtered = filtered.filter(annotation => annotation.tags.some(tag => includeSet.has(tag.toLowerCase())))
        }

        if (excludeTags?.length > 0) {
            const excludeSet = new Set(excludeTags.map(tag => tag.toLowerCase()))
            filtered = filtered.filter(annotation => !annotation.tags.some(tag => excludeSet.has(tag.toLowerCase())))
        }

        return filtered
	}, [filters, sourceAnnotations])
    const sortedAnnotations = useMemo(() => sortAnnotations(filteredAnnotations), [filteredAnnotations])
    const currentAnnotationRef = useRef<Annotation | undefined>(undefined)
    const sortedAnnotationsRef = useRef<Annotation[]>(sortedAnnotations)
    const isActiveRef = useRef(isActive)
    const reviewsRequestIdRef = useRef(0)

    const annotationIndex = sortedAnnotations.findIndex((annotation: Annotation) => annotation.id === currentAnnotation?.id)
    const totalEvents = sortedAnnotations.length

    const form = useEventReviewFormState({
        annotationReviews,
        sortedAnnotations,
        currentAnnotationReview,
    })

    const updateCurrentAnnotationReview = useCallback((review: AnnotationReview | undefined) => {
        setCurrentAnnotationReview(review)
        setUnmodifiedAnnotationReview(review)
    }, [])

    const addAnnotationReviewEntry = useCallback((reviewJSON: AnnotationReview) => {
        updateCurrentAnnotationReview(reviewJSON)
        setAnnotationReviews(previous => {
            const existingIndex = previous.findIndex(review => review.annotation_id === reviewJSON.annotation_id)
            if (existingIndex === -1) {
                return [...previous, reviewJSON]
            }
            return previous.map(review => (review.annotation_id === reviewJSON.annotation_id ? reviewJSON : review))
        })

        return endpointProvider.post(backendLinksProvider.LINKS.DATA.PROFILING.ADD_ANNOTATION_REVIEW, { annotation_review: reviewJSON })
    }, [backendLinksProvider, endpointProvider, updateCurrentAnnotationReview])

    const createDefaultAnnotationReview = useCallback((): AnnotationReview => {
        return {
            id: Date.now(),
            annotation_id: -1,
            review_type_id: 1,
            user_id: authProvider.currentUser.id,
            user_email: authProvider.currentUser.email,
            timestamp: Date.now(),
            is_complete: false,
            form: [
                {
                    id: EBOOSTQuestionID.IIC_PATTERN_TYPE,
                    type: QuestionResponseType.SINGLE_SELECT,
                    label: "Select predominant pattern",
                    options: [IICPatternType.LPD, IICPatternType.GPD, IICPatternType.LRDA, IICPatternType.OTHER],
                },
                {
                    id: EBOOSTQuestionID.IIC_PATTERN_LOCATION,
                    type: QuestionResponseType.SINGLE_SELECT,
                    label: "Location",
                    options: ["Left", "Right", "Bilat", "Bilateral-Independent", "Multifocal"],
                },
                {
                    id: EBOOSTQuestionID.IIC_PATTERN_PREVALENCE,
                    type: QuestionResponseType.SINGLE_SELECT,
                    label: "Prevalence",
                    options: [...PREVALENCE_OPTIONS],
                },
                {
                    id: EBOOSTQuestionID.IIC_PREDOMINANT_FREQUENCY,
                    type: QuestionResponseType.SINGLE_SELECT,
                    label: "Predominant frequency",
                    options: [...FREQUENCY_OPTIONS],
                },
                {
                    id: EBOOSTQuestionID.IIC_MAX_FREQUENCY,
                    type: QuestionResponseType.SINGLE_SELECT,
                    label: "Max frequency",
                    options: [...FREQUENCY_OPTIONS],
                },
                {
                    id: EBOOSTQuestionID.IIC_TIME_OF_MAX_FREQUENCY,
                    type: QuestionResponseType.DATETIME,
                    label: "Time point of max frequency",
                },
            ],
        }
    }, [authProvider])

    const createAnnotationReviewJSON = useCallback((annotationId: number): AnnotationReview => {
        const review = createDefaultAnnotationReview()
        review.annotation_id = annotationId
        review.is_complete = form.isComplete
        review.timestamp = Date.now()

        review.form.forEach(questionResponse => {
            switch (questionResponse.id) {
                case EBOOSTQuestionID.IIC_PATTERN_TYPE:
                    questionResponse.response = form.patternType
                    break
                case EBOOSTQuestionID.IIC_PATTERN_LOCATION:
                    questionResponse.response = form.location
                    break
                case EBOOSTQuestionID.IIC_PATTERN_PREVALENCE:
                    questionResponse.response = form.prevalence
                    break
                case EBOOSTQuestionID.IIC_PREDOMINANT_FREQUENCY:
                    questionResponse.response = form.predominantFrequency
                    break
                case EBOOSTQuestionID.IIC_MAX_FREQUENCY:
                    questionResponse.response = form.maxFrequency
                    break
                case EBOOSTQuestionID.IIC_TIME_OF_MAX_FREQUENCY:
                    questionResponse.response = form.timeOfMaxFrequency
                    break
                default:
                    break
            }
        })

        return review
    }, [
        createDefaultAnnotationReview,
        form.isComplete,
        form.location,
        form.maxFrequency,
        form.patternType,
        form.predominantFrequency,
        form.prevalence,
        form.timeOfMaxFrequency,
    ])

    const madeChangesToCurrentEventReview = useMemo(() => {
        if (!unmodifiedAnnotationReview?.form) {
            return false
        }

        const getResponses = (annotationReview: AnnotationReview): any[] => annotationReview.form.map(question => question.response)
        const currentReview = createAnnotationReviewJSON(-1)
        return !isEqual(getResponses(unmodifiedAnnotationReview), getResponses(currentReview))
    }, [createAnnotationReviewJSON, unmodifiedAnnotationReview])

    const handleSavingReview = useCallback(() => {
        if (form.firstError !== undefined) {
            return Promise.reject(`Save failed: ${form.firstError}`)
        }

        if (!currentAnnotationReview || !madeChangesToCurrentEventReview || form.formIsEmpty) {
            return Promise.resolve(0)
        }

        const reviewJSON = createAnnotationReviewJSON(currentAnnotationReview.annotation_id)
        return addAnnotationReviewEntry(reviewJSON)
    }, [addAnnotationReviewEntry, createAnnotationReviewJSON, currentAnnotationReview, form.firstError, form.formIsEmpty, madeChangesToCurrentEventReview])

    const saveReviewTask = useAsyncTask(handleSavingReview)

    const navigateToAnnotation = useCallback((annotation: Annotation, noSave: boolean = false) => {
        const startDate = new Date(annotation.start_time)
        const jumpType = isActive ? LinkedWindowEventType.JUMP_MIDDLE : LinkedWindowEventType.JUMP_START

        const doJump = () => {
            setCurrentAnnotation(annotation)
			jumpAllWindows(startDate, jumpType, { autoScale: true, controllerIsLinked: true })
        }

        if (noSave || (annotation.id === currentAnnotation?.id && !madeChangesToCurrentEventReview)) {
            doJump()
            return
        }

        saveReviewTask.run()
            .then(doJump)
            .catch(error => {
                if (isExpectedValidationSaveFailure(error)) {
                    return
                }
                console.error(error)
            })
    }, [currentAnnotation?.id, isActive, madeChangesToCurrentEventReview, saveReviewTask])

    const goToNextAnnotation = useCallback(() => {
        if (sortedAnnotations.length === 0) {
            return
        }

        if (annotationIndex >= sortedAnnotations.length - 1) {
            navigateToAnnotation(sortedAnnotations[0])
        } else {
            navigateToAnnotation(sortedAnnotations[annotationIndex + 1])
        }
    }, [annotationIndex, navigateToAnnotation, sortedAnnotations])

    const goToPreviousAnnotation = useCallback(() => {
        if (sortedAnnotations.length === 0) {
            return
        }

        if (annotationIndex <= 0) {
            navigateToAnnotation(sortedAnnotations[sortedAnnotations.length - 1])
        } else {
            navigateToAnnotation(sortedAnnotations[annotationIndex - 1])
        }
    }, [annotationIndex, navigateToAnnotation, sortedAnnotations])

    const reviewedEventIds = useMemo(() => {
        const ids = new Set<number>()

        for (const review of annotationReviews) {
            if (!review.is_complete) {
                continue
            }

            const found = sortedAnnotations.find(annotation => annotation.id === review.annotation_id)
            if (found) {
                ids.add(review.annotation_id)
            }
        }

        return ids
    }, [annotationReviews, sortedAnnotations])

    const incompleteEventCount = useMemo(() => {
        return sortedAnnotations.filter(annotation => !reviewedEventIds.has(annotation.id)).length
    }, [reviewedEventIds, sortedAnnotations])

    useEffect(() => {
        currentAnnotationRef.current = currentAnnotation
        sortedAnnotationsRef.current = sortedAnnotations
    }, [currentAnnotation, sortedAnnotations])

    useEffect(() => {
        isActiveRef.current = isActive
    }, [isActive])

    useEffect(() => {
        if (!isActive) {
            setLinkedWindowJumpType(LinkedWindowEventType.JUMP_END)
            setNavigationCursor(previous => {
                if (!previous.isVisible && previous.layoutGroupId === null && previous.cursorTime === null) {
                    return previous
                }

                return {
                    isVisible: false,
                    layoutGroupId: null,
                    cursorTime: null,
                }
            })
            return
        }

        setLinkedWindowJumpType(LinkedWindowEventType.JUMP_MIDDLE)
        setNavigationCursor(previous => {
            const nextLayoutGroupId = selectedLayoutGroup?.id ?? null

            if (previous.isVisible && previous.layoutGroupId === nextLayoutGroupId && previous.cursorTime === null) {
                return previous
            }

            return {
                isVisible: true,
                layoutGroupId: nextLayoutGroupId,
                cursorTime: null,
            }
        })
    }, [isActive, selectedLayoutGroup?.id, setLinkedWindowJumpType, setNavigationCursor])

    useEffect(() => {
        return () => {
            setSourceGroup(null)
            setActiveSession(null)
            setLinkedWindowJumpType(LinkedWindowEventType.JUMP_END)
            setNavigationCursor({
                isVisible: false,
                layoutGroupId: null,
                cursorTime: null,
            })
        }
    }, [setActiveSession, setLinkedWindowJumpType, setNavigationCursor, setSourceGroup])

    useEffect(() => {
        if (sourceGroup && !isActive) {
            setIsActive(true)
        }
    }, [sourceGroup, isActive])

    useEffect(() => {
		if (!isActive || sourceAnnotations.length === 0) {
            reviewsRequestIdRef.current += 1
            setAnnotationReviews([])
            setCurrentAnnotationReview(undefined)
            setUnmodifiedAnnotationReview(undefined)
            setIsRequestingReviews(false)
            return
        }

        setIsRequestingReviews(true)
        const requestId = reviewsRequestIdRef.current + 1
        reviewsRequestIdRef.current = requestId

        endpointProvider
			.post(backendLinksProvider.LINKS.DATA.PROFILING.GET_ANNOTATION_REVIEWS, {
				annotation_ids: sourceAnnotations.map(annotation => annotation.id),
			})
            .then((reviews: AnnotationReviews) => {
                if (reviewsRequestIdRef.current !== requestId || !isActiveRef.current) {
                    return
                }

                setAnnotationReviews(reviews)
                const selectedAnnotation = currentAnnotationRef.current

                if (!selectedAnnotation) {
                    const reviewedIds = new Set(
                        reviews
                            .filter(review => review.is_complete)
                            .map(review => review.annotation_id)
                    )
                    const firstIncomplete = sortedAnnotationsRef.current.find(annotation => !reviewedIds.has(annotation.id))
                    if (firstIncomplete) {
                        setCurrentAnnotation(firstIncomplete)
                    } else if (sortedAnnotationsRef.current.length > 0) {
                        setCurrentAnnotation(sortedAnnotationsRef.current[0])
                    }
                    return
                }

                const foundReview = reviews.find(review => review.annotation_id === selectedAnnotation.id)
                if (foundReview) {
                    updateCurrentAnnotationReview(foundReview)
                }
            })
            .catch(() => {
                if (reviewsRequestIdRef.current !== requestId || !isActiveRef.current) {
                    return
                }

                setAnnotationReviews([])
                if (!currentAnnotationRef.current && sortedAnnotationsRef.current.length > 0) {
                    setCurrentAnnotation(sortedAnnotationsRef.current[0])
                }
            })
            .finally(() => {
                if (reviewsRequestIdRef.current === requestId) {
                    setIsRequestingReviews(false)
                }
            })
	}, [backendLinksProvider, endpointProvider, isActive, sourceAnnotations, updateCurrentAnnotationReview])

    useEffect(() => {
        if (!isActive || !currentAnnotation || isRequestingReviews) {
            return
        }

        if (currentAnnotationReview?.annotation_id === currentAnnotation.id) {
            return
        }

        const existingReview = annotationReviews.find(review => review.annotation_id === currentAnnotation.id)
        if (existingReview) {
            updateCurrentAnnotationReview(existingReview)
            return
        }

        const newReview = createDefaultAnnotationReview()
        newReview.annotation_id = currentAnnotation.id
        setAnnotationReviews(previous => [...previous, newReview])
        updateCurrentAnnotationReview(newReview)
    }, [
        annotationReviews,
        createDefaultAnnotationReview,
        currentAnnotation,
        currentAnnotationReview?.annotation_id,
        isActive,
        isRequestingReviews,
        updateCurrentAnnotationReview,
    ])

    useEffect(() => {
        if (!currentAnnotation) {
            return
        }

        const foundAnnotation = sortedAnnotations.find(annotation => annotation.id === currentAnnotation.id)
        if (!foundAnnotation) {
            setCurrentAnnotation(undefined)
            setCurrentAnnotationReview(undefined)
        }
    }, [currentAnnotation, sortedAnnotations])

    useEffect(() => {
		const annotationIds = new Set(sourceAnnotations.map(annotation => annotation.id))
        setAnnotationReviews(previous => previous.filter(review => annotationIds.has(review.annotation_id)))
	}, [sourceAnnotations])

    const exitEventReview = useCallback(() => {
        const idsToUnload = [sourceGroup?.id?.toString(), activeSession?.id?.toString()].filter(Boolean) as string[]
        if (idsToUnload.length > 0) {
            setAnnotations(prev => prev.filter(a => !idsToUnload.includes(a.group_id?.toString())))
        }
        setSourceGroup(null)
        setActiveSession(null)
        setIsActive(false)
        setCurrentAnnotation(undefined)
        setCurrentAnnotationReview(undefined)
        setUnmodifiedAnnotationReview(undefined)
    }, [activeSession, setActiveSession, setAnnotations, setSourceGroup, sourceGroup])

    const sessionValue = useMemo<EventReviewSessionValue>(() => ({
        isActive,
        setIsActive,
        sourceGroup,
        currentAnnotation,
        setCurrentAnnotation,
        currentAnnotationReview,
        updateCurrentAnnotationReview,
        sortedAnnotations,
        annotationIndex,
        totalEvents,
        annotationReviews,
        setAnnotationReviews,
        addAnnotationReviewEntry,
        annotationsForRendering,
        exitEventReview,
        unmodifiedAnnotationReview,
        filters,
        setFilters,
        availableTags,
        isRequestingReviews,
        reviewedEventIds,
        incompleteEventCount,
    }), [
        addAnnotationReviewEntry,
        annotationIndex,
        annotationReviews,
        annotationsForRendering,
        exitEventReview,
        currentAnnotation,
        currentAnnotationReview,
        incompleteEventCount,
        isActive,
        isRequestingReviews,
        reviewedEventIds,
        sourceGroup,
        filters,
        setFilters,
        availableTags,
        sortedAnnotations,
        totalEvents,
        unmodifiedAnnotationReview,
        updateCurrentAnnotationReview,
    ])

    const formValue = useMemo<EventReviewFormValue>(() => ({
        patternType: form.patternType,
        setPatternType: form.setPatternType,
        location: form.location,
        setLocation: form.setLocation,
        prevalence: form.prevalence,
        setPrevalence: form.setPrevalence,
        predominantFrequency: form.predominantFrequency,
        setPredominantFrequency: form.setPredominantFrequency,
        maxFrequency: form.maxFrequency,
        setMaxFrequency: form.setMaxFrequency,
        timeOfMaxFrequency: form.timeOfMaxFrequency,
        setTimeOfMaxFrequency: form.setTimeOfMaxFrequency,
        isSetDateTimeActive: form.isSetDateTimeActive,
        setIsSetDateTimeActive: form.setIsSetDateTimeActive,
        firstError: form.firstError,
        errors: form.errors,
        updateValidationError: form.updateValidationError,
        resetValidation: form.resetValidation,
        isComplete: form.isComplete,
        formIsEmpty: form.formIsEmpty,
        navigateToAnnotation,
        goToNextAnnotation,
        goToPreviousAnnotation,
        saveReviewTask,
    }), [
        form.errors,
        form.firstError,
        form.formIsEmpty,
        form.isComplete,
        form.isSetDateTimeActive,
        form.location,
        form.maxFrequency,
        form.patternType,
        form.predominantFrequency,
        form.prevalence,
        form.resetValidation,
        form.setIsSetDateTimeActive,
        form.setLocation,
        form.setMaxFrequency,
        form.setPatternType,
        form.setPredominantFrequency,
        form.setPrevalence,
        form.setTimeOfMaxFrequency,
        form.timeOfMaxFrequency,
        form.updateValidationError,
        goToNextAnnotation,
        goToPreviousAnnotation,
        navigateToAnnotation,
        saveReviewTask,
    ])

    return (
        <EventReviewSessionContext.Provider value={sessionValue}>
            <EventReviewFormContext.Provider value={formValue}>
                {children}
            </EventReviewFormContext.Provider>
        </EventReviewSessionContext.Provider>
    )
}

export const useEventReview = () => useContext(EventReviewSessionContext)
export const useEventReviewForm = () => useContext(EventReviewFormContext)
